'use client';
import { useLenis } from 'lenis/react';
import lottie, { AnimationItem } from 'lottie-web';
import Link from 'next/link';
import { useCallback, useEffect, useRef } from 'react';

import { FullLogo } from '@/components/full-logo';
import animation from '@/components/lottie/files/footer-reverse-desktop.json';
import animation_mobile from '@/components/lottie/files/footer-reverse-mobile_9x20.json';
import { useAspectRatio } from '@/helpers/hooks';


export const AnimatedFooter = () => {
  const animationContainerRef = useRef<HTMLDivElement | null>(null);
  const animationInstance = useRef<AnimationItem | null>(null);
  const currentYear = new Date().getFullYear();

  const { isLandscape } = useAspectRatio();

  // Defined before the useEffect that depends on it to avoid TDZ in minified output
  const handleScroll = useCallback(() => {
    if (!animationContainerRef.current || !animationInstance.current) return;

    const rect = animationContainerRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const containerHeight = rect.height;
    const startOffset = containerHeight * 0.1;
    const scrolled = windowHeight - rect.top - startOffset;

    if (scrolled < 0) {
      animationInstance.current.goToAndStop(0, true);
      return;
    }

    const scrollableDistance = containerHeight * 1.5;
    const progress = Math.max(0, Math.min(1, scrolled / scrollableDistance));
    const targetFrame = progress * animationInstance.current.totalFrames;

    animationInstance.current.goToAndStop(targetFrame, true);
  }, []);

  useEffect(() => {
    if (!animationContainerRef.current) return;

    const instance = lottie.loadAnimation({
      container: animationContainerRef.current,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      animationData: isLandscape ? animation : animation_mobile,
    });

    animationInstance.current = instance;
    // Sync to current scroll position once SVG is ready.
    // Needed when isLandscape flips on hydration and Lenis doesn't
    // fire a new scroll event for the freshly loaded instance.
    instance.addEventListener('DOMLoaded', handleScroll);

    return () => {
      instance.removeEventListener('DOMLoaded', handleScroll);
      instance.destroy();
      animationInstance.current = null;
    };
  }, [isLandscape, handleScroll]);

  useLenis(handleScroll);

  return (
    <div className="relative h-dvh w-full bg-dark" style={{ zIndex: 30 }}>
      {/* Animation Layer - Behind */}
      <div className="absolute inset-0 h-full w-full" ref={animationContainerRef} style={{ zIndex: 1 }} />

      {/* Content Layer - On Top */}
      <div className="absolute inset-0 h-full overscroll-y-contain" style={{ zIndex: 10 }}>
        <div className="flex h-full items-end justify-center">
          <div className="mx-container w-full desktop:w-10/12 portrait:pb-[5dvh] landscape:pb-[5dvh]">
            <div className="grid w-full grid-cols-1 text-center text-[14px] tablet:grid-cols-2 tablet:text-left desktop:text-[11px] desktopXL:text-[18px] desktop4k:text-[22px]">
              <ul className="mb-[12px] flex justify-center space-x-4 text-white tablet:mb-0 tablet:justify-start desktop:space-x-6 desktopXL:space-x-[24px]">
                <li className="cursor-pointer transition-colors hover:text-at hover:underline">
                  <a rel="noopener noreferrer" href="https://dribbble.com/allturtles">
                    Dribbble
                  </a>
                </li>
                <li className="cursor-pointer transition-colors hover:text-at hover:underline">
                  <a rel="noopener noreferrer" href="https://www.linkedin.com/company/allturtlesco/mycompany/">
                    Linkedin
                  </a>
                </li>
              </ul>
              <div className="flex min-w-0 flex-col items-center gap-1 text-white tablet:min-w-max tablet:flex-row tablet:flex-nowrap tablet:justify-end tablet:gap-0 tablet:text-right">
                <span className="tablet:shrink-0">© {currentYear} All Turtles Corporation.</span>
                <span className="whitespace-nowrap tablet:ml-2 tablet:shrink-0">
                  <Link
                    href="/privacy"
                    className="ml-2 cursor-pointer underline transition-colors first:ml-0 hover:text-at"
                  >
                    Privacy
                  </Link>
                  <Link href="/terms" className="ml-2 cursor-pointer underline transition-colors hover:text-at">
                    Terms
                  </Link>
                  <Link
                    href="/patents-and-trademarks"
                    className="ml-2 cursor-pointer underline transition-colors hover:text-at"
                  >
                    Patents and Trademarks
                  </Link>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
